import {
  selectCitiesCache,
  selectCurrentPlayer,
  selectGridConfigState,
  selectHexes,
  selectPlayers,
  selectPolicyMarket,
  selectStartingQuarters
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {setStartingQuarter} from "../../../redux/actions";
import {getPlayerFlagToken} from "../../players/getPlayerFlagToken";
import {getRandomHexCoordsForStartingQuarter} from "../../gameState/setup/getRandomHexCoordsForStartingQuarter";
import {calcBfsDistances, getBotGridRenderer, getNeighborHexKeys} from "../botGrid";
import {checkIfKeyPointToken, getKeyPointValuePerRound} from "../evaluation/keyPoints";
import {KEY_POINT_TOKENS} from "../../../constants/tokens";
import {getEnemyQuarterHexKeys} from "../evaluation/cities";
import {getBotStrategy} from "../strategy/botStrategyStore";
import {BOT_ARCHETYPES} from "../strategy/archetypes";
import {getBotProfile} from "../strategy/getBotProfile";
import {keyToCoords} from "../../coords/keyToCoords";
import {getFactionPreferences} from "../strategy/factionPreferences";
import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {calcPolicyPrice} from "../../policy/calcPolicyPrice";
import {POLICIES} from "../../../constants/policies";
import {FACTIONS} from "../../../constants/factions";
import {NEUTRAL_PLAYER_COLOR} from "../../../constants/players";
import {getRandomElement} from "../../other/getRandomElement";

// Веса агрессивного размещения: раш стартует у самого центра/противников
const SETUP_CENTER_PER_CELL_RUSH = 0.8;
const SETUP_CENTER_PER_CELL_AGGRESSIVE = 0.4;
const SETUP_ENEMY_PROXIMITY_PER_CELL = 0.5;
const SETUP_ENEMY_PROXIMITY_RANGE = 8;

// Волна 17 (живой тест 16.2): не-rush боты уступают центр rush-фракциям
const SETUP_CENTER_AVOID_PER_CELL = 0.4;
// Волна 17 (16.11): олигархия-опенинг — тыловой якорь у нейтрального города
// (гильдии получат пути влияния); фракции и вес бонуса близости
const OLIGARCHY_SETUP_FACTIONS = new Set([
  FACTIONS.VENTORY, FACTIONS.ENLIGHTENED, FACTIONS.ELEMENTALS,
]);
const SETUP_OLIGARCHY_ANCHOR_PER_CELL = 1.5;
const SETUP_OLIGARCHY_ANCHOR_RANGE = 5;

// Ключевые точки в сетапе оцениваются по многораундовой ценности (доход за всю
// партию), иначе дебютный доход тонет в позиционных бонусах:
// смежно ЦК — захват сразу; через квартал 1 раунда — с дисконтом за действие
const SETUP_IMMEDIATE_KEY_POINT_FACTOR = 12;
const SETUP_QUARTER_KEY_POINT_FACTOR = 8;

/**
 * Фаза SETUP: выбрать ячейку с флагом своего цвета для стартового центрального квартала.
 * Оценка: ключевые точки смежно (будут сразу контролироваться) x3, на дальности 2 x1.
 * Агрессивные архетипы (особенно Раш) дополнительно тянутся к центру карты и к
 * нейтральным/вражеским городам — стартовать на периферии им противопоказано
 * (в т.ч. центральная политика HEGEMONISM иначе бесполезна).
 */
// Разница оценок меньше этой — погрешность сложения дробных весов
const SETUP_SCORE_TIE_EPSILON = 1e-9;

/**
 * Лучший флаг старта, из равных — случайный. На симметричных полях у места есть
 * равные зеркальные флаги, а первый по порядку ячеек всегда лежит западнее: так
 * восточные места садились ближе к центру, западные — к краю, и индекс побед мест
 * симметричного поля расходился втрое (отчет 40)
 */
export const pickBestSetupFlag = (flags) => {
  const bestScore = Math.max(...flags.map((flag) => flag.score));
  return getRandomElement(flags.filter((flag) => bestScore - flag.score < SETUP_SCORE_TIE_EPSILON));
};

export const decideSetupStep = (store) => {
  const state = store.getState();
  const currentPlayer = selectCurrentPlayer(state);
  const startingQuarters = selectStartingQuarters(state);

  if (startingQuarters[currentPlayer.id]) {
    // Квартал уже размещен — далее завершение хода
    return false;
  }

  const hexKey = chooseStartingQuarterHexKey(state, currentPlayer);
  if (!hexKey) {
    return false;
  }
  store.dispatch(setStartingQuarter(currentPlayer.id, hexKey));
  return true;
};

/**
 * Выбор ячейки стартового квартала для ПРОИЗВОЛЬНОГО игрока (не обязательно
 * текущего) — тот же скоринг, что у decideSetupStep. Используется сервером для
 * ботов в одновременной фазе SETUP сетевой игры. Возвращает hexKey или null.
 */
export const chooseStartingQuarterHexKey = (state, player) => {
  const hexes = selectHexes(state);
  const flagToken = getPlayerFlagToken(player);
  const flagHexKeys = Object.entries(hexes)
    .filter(([, token]) => token === flagToken)
    .map(([hexKey]) => hexKey);

  if (flagHexKeys.length === 0) {
    return getRandomHexCoordsForStartingQuarter(hexes, player) || null;
  }

  const gridRenderer = getBotGridRenderer(state);

  // Агрессивная составляющая размещения: центр карты + близость к чужим городам
  const strategy = getBotStrategy(player.id);
  const profile = getBotProfile(state, player);
  const centerWeight = strategy.archetype === BOT_ARCHETYPES.AGGRESSIVE_RUSH
    ? SETUP_CENTER_PER_CELL_RUSH
    : (profile.military > 1 ? SETUP_CENTER_PER_CELL_AGGRESSIVE : 0);

  const fieldRadius = selectGridConfigState(state)?.radius || 0;
  const citiesCache = selectCitiesCache(state);
  const enemyQuarterHexKeys = [...getEnemyQuarterHexKeys(citiesCache, player.color).keys()];
  const enemyDistances = centerWeight > 0 && enemyQuarterHexKeys.length > 0
    ? calcBfsDistances(enemyQuarterHexKeys, hexes, gridRenderer)
    : null;

  // Волна 19 (живой тест 18.6, запечено): демоны на больших картах (5+ игроков)
  // стартуют максимально близко к центру — их аура и Великие руины в гуще событий
  const isDemonsCenterSetup = player.factionId === FACTIONS.DEMONS
    && selectPlayers(state).length >= 5;
  const demonsCenterWeight = isDemonsCenterSetup ? SETUP_CENTER_PER_CELL_RUSH * 2 : 0;

  // Живой тест 16.2: не-rush бот при rush-фракции в партии не лезет в центр —
  // стартовый город в центре попадает под раннее давление раша
  let centerAvoidWeight = 0;
  if (strategy.archetype !== BOT_ARCHETYPES.AGGRESSIVE_RUSH && !isDemonsCenterSetup) {
    const hasRushOpponent = selectPlayers(state).some((otherPlayer) =>
      otherPlayer.id !== player.id
      && getFactionPreferences(otherPlayer.factionId).archetypes?.[0] === BOT_ARCHETYPES.AGGRESSIVE_RUSH
    );
    if (hasRushOpponent) {
      centerAvoidWeight = SETUP_CENTER_AVOID_PER_CELL;
    }
  }

  // При дешевой OLIGARCHY на рынке вентори/просвещенные/элементали якорятся у
  // нейтрального города — будущие пути влияния гильдий (16.11).
  // Волна 18 (17.1): усиленный вес якоря — олигархия-опенинг в живом тесте не
  // проявлялся, стартовый город должен уверенно вставать в тыл к нейтралу
  let oligarchyAnchorDistances = null;
  let oligarchyAnchorWeight = SETUP_OLIGARCHY_ANCHOR_PER_CELL;
  if (OLIGARCHY_SETUP_FACTIONS.has(player.factionId)) {
    const policyMarket = selectPolicyMarket(state) || [];
    const oligarchyIndex = policyMarket.findIndex((policy) => policy?.id === POLICIES.OLIGARCHY);
    if (oligarchyIndex >= 0 && calcPolicyPrice(oligarchyIndex, player) <= 3) {
      const neutralQuarterKeys = (citiesCache?.[NEUTRAL_PLAYER_COLOR] || [])
        .flatMap((city) => getAllQuartersOfCity(city));
      if (neutralQuarterKeys.length > 0) {
        oligarchyAnchorDistances = calcBfsDistances(neutralQuarterKeys, hexes, gridRenderer);
        // Волна 18 запечена: усиленный вес якоря безусловно
        oligarchyAnchorWeight = SETUP_OLIGARCHY_ANCHOR_PER_CELL * 2;
      }
    }
  }

  const flagStats = [];

  flagHexKeys.forEach((flagHexKey) => {
    let score = 0;
    // Счетчики рудничной цели дебюта: 2 обычных рудника или 1 великий
    let regularMinesCaptured = 0;
    let superMinesCaptured = 0;
    const countMineToken = (token) => {
      if (token === KEY_POINT_TOKENS.MINE) regularMinesCaptured += 1;
      if (token === KEY_POINT_TOKENS.SUPER_MINE) superMinesCaptured += 1;
    };

    const ring1 = getNeighborHexKeys(flagHexKey, gridRenderer);

    // Точки, которые центральный квартал захватит сразу
    const immediateKeyPoints = new Set();
    ring1.forEach((hexKey) => {
      if (checkIfKeyPointToken(hexes[hexKey])) {
        immediateKeyPoints.add(hexKey);
        score += getKeyPointValuePerRound(hexes[hexKey]) * SETUP_IMMEDIATE_KEY_POINT_FACTOR;
        countMineToken(hexes[hexKey]);
      }
    });

    // До двух базовых кварталов 1 раунда: каждый ставится на СВОБОДНУЮ ячейку
    // кольца-1 и должен захватывать РАЗНЫЕ, еще не посчитанные точки
    // (2 обычных рудника двумя кварталами или великий рудник одним — по рецепту)
    const quarterOptions = [];
    ring1.forEach((cellKey) => {
      if (hexes[cellKey]) {
        return; // ячейка занята — квартал не поставить
      }
      const capturableKeyPoints = getNeighborHexKeys(cellKey, gridRenderer).filter((adjKey) =>
        checkIfKeyPointToken(hexes[adjKey]) && !immediateKeyPoints.has(adjKey)
      );
      if (capturableKeyPoints.length > 0) {
        quarterOptions.push({cellKey, keyPoints: capturableKeyPoints});
      }
    });

    // Жадно выбираем до 2 кварталов без пересечения по точкам
    const countedKeyPoints = new Set(immediateKeyPoints);
    for (let pick = 0; pick < 2; pick++) {
      let bestOption = null;
      let bestValue = 0;
      quarterOptions.forEach((option) => {
        const value = option.keyPoints.reduce((sum, kpKey) =>
          sum + (countedKeyPoints.has(kpKey) ? 0 : getKeyPointValuePerRound(hexes[kpKey])), 0);
        if (value > bestValue) {
          bestValue = value;
          bestOption = option;
        }
      });
      if (!bestOption) {
        break;
      }
      score += bestValue * SETUP_QUARTER_KEY_POINT_FACTOR;
      bestOption.keyPoints.forEach((kpKey) => {
        if (!countedKeyPoints.has(kpKey)) {
          countMineToken(hexes[kpKey]);
        }
        countedKeyPoints.add(kpKey);
      });
    }

    const [q, r] = keyToCoords(flagHexKey);
    const distanceFromCenter = (Math.abs(q) + Math.abs(r) + Math.abs(q + r)) / 2;

    if (centerWeight > 0 && fieldRadius > 0) {
      score += Math.max(0, fieldRadius - distanceFromCenter) * centerWeight;

      // Фланги рядом с нейтральными городами и противниками — тоже боевой старт
      if (enemyDistances) {
        const enemyDistance = enemyDistances.get(flagHexKey);
        if (enemyDistance !== undefined) {
          score += Math.max(0, SETUP_ENEMY_PROXIMITY_RANGE - enemyDistance) * SETUP_ENEMY_PROXIMITY_PER_CELL;
        }
      }
    }

    // Уступка центра при rush-фракциях в партии (16.2)
    if (centerAvoidWeight > 0 && fieldRadius > 0) {
      score -= Math.max(0, fieldRadius - distanceFromCenter) * centerAvoidWeight;
    }

    // Волна 19 (18.6): демоны на картах 5+ игроков — доминирующий вес центра
    if (demonsCenterWeight > 0 && fieldRadius > 0) {
      score += Math.max(0, fieldRadius - distanceFromCenter) * demonsCenterWeight;
    }

    // Тыловой якорь олигархия-опенинга у нейтрального города (16.11; вес усилен
    // в волне 18 — 17.1)
    if (oligarchyAnchorDistances) {
      const anchorDistance = oligarchyAnchorDistances.get(flagHexKey);
      score += Math.max(0, SETUP_OLIGARCHY_ANCHOR_RANGE - (anchorDistance ?? Infinity))
        * oligarchyAnchorWeight;
    }

    flagStats.push({
      flagHexKey,
      score,
      distanceFromCenter,
      isMineGoalAchieved: regularMinesCaptured >= 2 || superMinesCaptured >= 1,
    });
  });

  // Агрессивные профили: стартовый город — центр найма и роста отрядов, он должен
  // стоять как можно ближе к зоне агрессии середины игры. Из флагов, выполняющих
  // рудничную цель, берем ближайший к центру карты (скоринг — только тайбрейк).
  // Волна 17 (16.2): при уступке центра шорткат не работает — решает скоринг
  const goalFlags = flagStats.filter((flag) => flag.isMineGoalAchieved);
  if (centerWeight > 0 && centerAvoidWeight === 0 && goalFlags.length > 0) {
    const nearestDistance = Math.min(...goalFlags.map((flag) => flag.distanceFromCenter));
    return pickBestSetupFlag(goalFlags.filter((flag) => flag.distanceFromCenter === nearestDistance)).flagHexKey;
  }

  return pickBestSetupFlag(flagStats).flagHexKey;
};
